import { useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { dividiTesto, oggiARoma, righeScritte, TITOLO_MAX, type ProgettoDivisore, type RispostaDividi } from '@masamune/shared';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { Mascotte } from '../ui/Mascotte';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useAuthStore } from '../../stores/auth.store';
import { useIsMobile } from '../../hooks/useIsMobile';
import {
  assignableUsersQuery, clientsLiteQuery,
  RECURRENCES, RECURRENCE_LABEL, fmtDateTime, fromDateTimeInput, fullName,
  type TaskRecurrenceKey,
} from '../../features/tasks/tasksApi';
import { useTaskActions, type EsitoLotto } from '../../features/tasks/useTaskActions';
import { annullaAzione, dividi, puoRispondere, statoAssistenteQuery } from '../../features/assistente/assistenteApi';
import { rinfrescaSpesa } from '../../features/assistente/registroApi';
import { leggibile } from '../mobile/MobileKit';
import {
  AnteprimaTask, corpoDi, nomeBreve, righeAnteprima,
  type DallaFinestra, type ExtraProposta, type Scelte,
} from './AnteprimaTask';
import { OpzioniPersone, isProfessionista } from '../../features/tasks/OpzioniPersone';

interface Prefill {
  title?: string;
  description?: string;
  priority?: string;
  /** Più righe incollate nell'aggiunta veloce: il testo da dividere in task. */
  testo?: string;
  /** "Assegnato a" già scelto altrove ('' = nessuno; se manca, chi crea). */
  assigneeId?: string;
  /** "Scadenza" già scelta altrove, 'YYYY-MM-DD'. */
  dueDate?: string;
  /** "Cliente" già scelto: con «Dividi in più task» quello della task di partenza (28/10/2026). */
  clientId?: string;
  /** «Dividi in più task» (28/10/2026): la task da cui si parte, da completare quando si creano le nuove. */
  partenzaId?: string;
  /**
   * La proposta dell'Assistente già chiesta dal menu ⋯ della task (POST /ai/dividi su
   * `testo`, cioè titolo e descrizione): la finestra si apre già in anteprima (28/10/2026).
   */
  proposta?: RispostaDividi;
  /**
   * La task di partenza è un lavoro previsto (inScope): completarla la conta fatta nel
   * resoconto al cliente, quindi la spunta «Completa» parte tolta (28/10/2026).
   */
  partenzaPrevista?: boolean;
  /** La task di partenza si ripete: completarla fa nascere subito la prossima. */
  partenzaRicorrente?: boolean;
}

/** Il server crea al massimo 50 task alla volta (createTasksBulkInput). */
const MAX_TASK = 50;
/** Il testo più lungo che l'Assistente divide in una volta (dividiInput). */
const MAX_DETTATO = 8000;

// I progetti servono al divisore: "Villa Fago — Digital Marketing 2026" porta al cliente GVF.
// Chi non vede i progetti li ha vuoti, e il divisore fa a meno di loro.
const progettiLiteQuery = {
  queryKey: ['tasks', 'projects-lite'] as const,
  queryFn: async (): Promise<ProgettoDivisore[]> => {
    const r = await api.get<{ items?: Array<{ id: string; name: string; client?: { id: string } | null }> }>('/projects?pageSize=200');
    return (r.items ?? []).map((p) => ({ id: p.id, name: p.name, clientId: p.client?.id ?? null }));
  },
  staleTime: 5 * 60_000,
  retry: false,
};

/**
 * Più frasi di fila ("… per Graci. Poi chiamare …"): è un dettato, non un titolo. Con
 * questo, o oltre i 200 caratteri, «Dividi con MUNE» è il tasto principale
 * (os-planning/ai/PIANO.md, 1.1). Basta una frase che finisce e una che comincia.
 */
const piuFrasi = (t: string) => /[.!?]\s+\p{Lu}/u.test(t);

/** "a", "a e b", "a, b e c". */
const elenco = (parti: string[]) => (parti.length < 2 ? parti.join('') : `${parti.slice(0, -1).join(', ')} e ${parti[parti.length - 1]}`);

/**
 * Sotto «Completa la task di partenza»: cosa succede completandola, quando non è solo
 * chiuderla (28/10/2026, dalla revisione). Un lavoro previsto completato risulta fatto nel
 * resoconto al cliente; una task che si ripete fa nascere subito la prossima.
 */
function notaPartenza(prevista: boolean, ricorrente: boolean): string | null {
  if (prevista && ricorrente) return 'È un lavoro previsto che si ripete: completarlo lo conta fatto nel resoconto al cliente e fa nascere subito il prossimo';
  if (prevista) return 'È un lavoro previsto: completarlo lo conta fatto nel resoconto al cliente';
  if (ricorrente) return 'Si ripete: completarla fa nascere subito la prossima';
  return null;
}

/** «Crea 3 task e ricorda 2 cose», «Crea 3 task», «Ricorda 2 cose» o «Niente da creare». */
function etichettaProposta(n: number, m: number, inCorso: boolean): string {
  const cose = m === 1 ? 'una cosa' : `${m} cose`;
  if (inCorso) return n > 0 ? `Creo ${n} task…` : `Ricordo ${cose}…`;
  if (n > 0 && m > 0) return `Crea ${n} task e ricorda ${cose}`;
  if (n > 0) return `Crea ${n} task`;
  if (m > 0) return `Ricorda ${cose}`;
  return 'Niente da creare';
}

/**
 * Dopo «Crea 3 task e ricorda 2 cose» un toast solo, con l'Annulla dell'azione
 * dell'Assistente (fase-1-progetto.md, decisione 11): 6 secondi, poi l'Annulla resta in
 * «Cosa ha fatto MUNE». L'Annulla rimette solo quello che nessuno ha ancora
 * cambiato, e la frase dell'esito lo dice. La finestra è già chiusa: qui niente stato suo.
 */
function toastDellaProposta(r: EsitoLotto, aiActionId: string, qc: QueryClient) {
  const ricordate = r.ricordate ?? 0;
  const cose = ricordate === 1 ? 'una cosa' : `${ricordate} cose`;
  const titolo = r.count > 0
    ? (r.count === 1 ? 'Task creato' : `${r.count} task creati`)
    : `${ricordate === 1 ? 'Ricordata' : 'Ricordate'} ${cose}`;
  toast.undo(titolo, {
    description: r.count > 0 && ricordate > 0 ? `e ${ricordate === 1 ? 'ricordata' : 'ricordate'} ${cose}` : undefined,
    onUndo: () => annullaDalToast(r.aiActionId ?? aiActionId, qc),
  });
}

/** L'Annulla da un toast: com'è andata si legge in un altro toast, la finestra è già chiusa. */
function annullaDalToast(aiActionId: string, qc: QueryClient) {
  annullaAzione(aiActionId)
    .then((esito) => {
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['project'] });
      qc.invalidateQueries({ queryKey: ['ai', 'azioni'] });
      qc.invalidateQueries({ queryKey: ['ai', 'memoria'] });
      if (esito.stato === 'ANNULLATA') toast.ok(esito.frase);
      else toast.warn(esito.frase);
    })
    .catch((e) => toast.alert('Annullamento non riuscito', e instanceof ApiError ? e.message : 'Riprova'));
}

/**
 * Nuovo task: solo l'essenziale. Titolo, assegnato a (default: chi crea),
 * scadenza, promemoria e ricorrenza facoltativi, priorità Normale/Alta,
 * descrizione facoltativa, cliente in fondo. Il progetto non si sceglie qui:
 * arriva già impostato quando si crea dalla kanban di progetto.
 * Salva con Ctrl/Cmd+Invio.
 *
 * Più cose insieme (7/10/2026): il titolo va a capo, e da due righe in su
 * ogni riga è un task. Sotto il titolo si vede cosa diventa ciascuna (cliente,
 * persona, scadenza, urgenza letti dal divisore, packages/shared/tasks), si
 * corregge, e un tasto solo "Crea N task" li crea tutti (POST /tasks/bulk).
 * Con una riga sola la finestra, la chiamata e il toast sono quelli di prima.
 *
 * L'Assistente (28/10/2026, os-planning/ai/fase-1-progetto.md, decisioni 10 e 11):
 * agli Amministratori, quando l'Assistente può rispondere, sotto il titolo c'è
 * «Dividi con MUNE». Un dettato tutto di fila diventa una proposta (POST
 * /ai/dividi) che si vede nella stessa anteprima, anche con un task solo, con le cose
 * da aggiungere alle schede dei clienti. Si salva con un tasto solo, col lotto e l'id
 * della proposta, e il toast ha l'Annulla. «Dividi in più task» (menu ⋯ della task)
 * apre la finestra con la proposta già fatta e la spunta che completa la task di
 * partenza. Ad Assistente spento la finestra è quella di prima.
 */
export function NewTaskModal({ onClose, projectId, prefill }: { onClose: () => void; projectId?: string; prefill?: Prefill }) {
  const qc = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const titoloIniziale = prefill?.testo ?? prefill?.title ?? '';
  const [form, setForm] = useState({
    title: titoloIniziale,
    description: prefill?.description ?? '',
    assigneeId: prefill?.assigneeId !== undefined ? prefill.assigneeId : me?.id ?? '',
    dueDate: prefill?.dueDate ?? '',
    remindAt: '',
    recurrence: 'NONE' as TaskRecurrenceKey,
    high: prefill?.priority === 'ALTA',
    clientId: prefill?.clientId ?? '',
  });

  const { data: clients } = useQuery(clientsLiteQuery);
  const { data: users } = useQuery(assignableUsersQuery);
  const isMobile = useIsMobile();
  const [altri, setAltri] = useState(false);
  const mostraAltri = !isMobile || altri;

  // L'Assistente: solo agli Amministratori, e solo quando può rispondere. Per gli altri
  // GET /ai/stato dà 403: la query non parte nemmeno, e la finestra resta quella di prima.
  const amministratore = me?.role.key === 'SUPER_ADMIN';
  const { data: statoAi } = useQuery({ ...statoAssistenteQuery, enabled: amministratore });
  const conAssistente = amministratore && puoRispondere(statoAi);
  // La proposta sta da sola (decisione 11): `divisi` si rifà a ogni tasto, lei no. `testo` è
  // quello da cui è nata: se la persona lo cambia, la proposta si toglie e torna l'anteprima a regole.
  const [proposta, setProposta] = useState<{ r: RispostaDividi; testo: string } | null>(
    () => (prefill?.proposta ? { r: prefill.proposta, testo: titoloIniziale } : null),
  );
  // C'è già stata una proposta: il tasto diventa «Dividi di nuovo con MUNE».
  const [giaDiviso, setGiaDiviso] = useState(!!prefill?.proposta);
  // Le cose da ricordare tolte a mano, per indice nella proposta: partono tutte spuntate.
  const [ricordaTolte, setRicordaTolte] = useState<ReadonlySet<number>>(() => new Set());
  // «Completa la task di partenza quando crei queste»: già spuntata, tranne per un lavoro
  // previsto (28/10/2026, dalla revisione): lo segnerebbe fatto nel resoconto al cliente,
  // mentre il lavoro continua nelle task nuove, che non sono lavori previsti.
  const completaDiPartenza = !prefill?.partenzaPrevista;
  const [completaPartenza, setCompletaPartenza] = useState(completaDiPartenza);
  const [erroreAi, setErroreAi] = useState<string | null>(null);
  const partenzaId = prefill?.partenzaId ?? null;

  // Una riga = un task, come sempre. Da due righe scritte in su: l'anteprima. Con una
  // proposta dell'Assistente l'anteprima c'è anche con un task solo.
  const multi = righeScritte(form.title) >= 2;
  const conAnteprima = multi || !!proposta;
  const { data: progetti } = useQuery({ ...progettiLiteQuery, enabled: multi });
  const [scelte, setScelte] = useState<Record<string, Scelte>>({});
  const [errore, setErrore] = useState<string | null>(null);
  const oggi = useMemo(() => oggiARoma(), []);
  const contesto = useMemo(
    () => ({ oggi, io: me?.id ?? '', persone: users ?? [], clienti: clients?.items ?? [], progetti: progetti ?? [] }),
    [oggi, me?.id, users, clients, progetti],
  );
  const divisiRegole = useMemo(
    () => (multi && !proposta ? dividiTesto(form.title, contesto) : []),
    [multi, proposta, form.title, contesto],
  );
  const divisi = proposta ? proposta.r.tasks : divisiRegole;
  const righe = useMemo(
    () => righeAnteprima(
      divisi,
      scelte,
      { clientId: form.clientId || null, assigneeId: form.assigneeId || null, dueDate: form.dueDate || null, alta: form.high },
      !!proposta,
    ),
    [divisi, scelte, form.clientId, form.assigneeId, form.dueDate, form.high, proposta],
  );
  const opz = useMemo(() => ({
    oggi,
    meId: me?.id ?? '',
    clienti: (clients?.items ?? []).map((c) => {
      const nome = leggibile(c.shortName || c.legalName);
      return { id: c.id, nome, breve: nomeBreve(nome) };
    }),
    // Prima la squadra, poi i professionisti esterni, con la parola (vedono solo le loro task).
    persone: [...(users ?? [])].sort((a, b) => Number(isProfessionista(a)) - Number(isProfessionista(b))).map((u) => ({
      id: u.id,
      nome: `${fullName(u)}${u.id === me?.id ? ' (io)' : ''}${isProfessionista(u) ? ' · professionista' : ''}`,
      breve: `${u.firstName.charAt(0)}. ${u.lastName}`,
    })),
  }), [oggi, me?.id, clients, users]);

  const cambia = (chiave: string | null, s: Scelte) => {
    setErrore(null);
    setScelte((prima) => {
      if (chiave) return { ...prima, [chiave]: { ...prima[chiave], ...s } };
      // "Spunta tutte" / "Togli tutte le spunte": su ogni riga che si può creare.
      const tutte = { ...prima };
      for (const r of righe) if (!r.problema) tutte[r.chiave] = { ...tutte[r.chiave], ...s };
      return tutte;
    });
  };

  // «Dividi con MUNE»: la chiamata a pagamento parte solo da un clic, mai da un effetto.
  const divide = useMutation({
    mutationFn: (testo: string) => dividi({
      testo,
      projectId: projectId ?? null,
      clientId: form.clientId || null,
      partenzaId,
      pagina: window.location.pathname,
    }),
    onSuccess: (r, testo) => {
      setProposta({ r, testo });
      // Le correzioni sono legate al testo delle righe: quelle fatte prima non valgono qui.
      setScelte({});
      setRicordaTolte(new Set());
      setCompletaPartenza(completaDiPartenza);
      setErrore(null);
      setGiaDiviso(true);
      // La chiamata è pagata: la spesa in Impostazioni si rifà (28/10/2026).
      rinfrescaSpesa(qc);
      // Il tasto premuto sparisce e il fuoco cadrebbe fuori dalla finestra: Ctrl+Invio non
      // creerebbe niente. Al telefono no, aprirebbe la tastiera sopra l'anteprima.
      if (!isMobile) titoloRef.current?.focus({ preventScroll: true });
    },
    // Budget finito (402), troppe richieste, nessuna risposta: la frase del server, così com'è.
    onError: (e) => setErroreAi(e instanceof ApiError ? e.message : 'MUNE non ha risposto: riprova o fallo a mano'),
  });
  const dividendo = divide.isPending;
  const chiediDivisione = () => {
    if (dividendo || !form.title.trim()) return;
    setErroreAi(null);
    if (form.title.trim().length > MAX_DETTATO) {
      setErroreAi(`Testo troppo lungo per MUNE: al massimo ${MAX_DETTATO.toLocaleString('it-IT')} caratteri alla volta`);
      return;
    }
    divide.mutate(form.title);
  };
  const cambiaTitolo = (testo: string) => {
    setErrore(null);
    setErroreAi(null);
    // Il testo non è più quello della proposta: la proposta si toglie e torna l'anteprima a regole.
    if (proposta && testo !== proposta.testo) {
      setProposta(null);
      setScelte({});
    }
    setForm({ ...form, title: testo });
  };

  // Una riga sola: il titolo così com'è, anche se c'è un a capo vuoto o un puntino da solo.
  const titoloSolo = form.title.replace(/\s*\n\s*/g, ' ').trim();
  const create = useMutation({
    mutationFn: () => api.post<{ id: string }>('/tasks', {
      title: titoloSolo,
      description: form.description.trim() || null,
      projectId: projectId ?? null,
      clientId: form.clientId || null,
      status: 'TODO',
      priority: form.high ? 'ALTA' : 'MEDIA',
      assigneeId: form.assigneeId || null,
      dueDate: form.dueDate || null,
      remindAt: fromDateTimeInput(form.remindAt),
      recurrence: form.recurrence,
      isPrivate: false,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['project'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      toast.ok('Task creato', titoloSolo);
      onClose();
    },
    onError: (err) => toast.alert('Task non creato', err instanceof ApiError ? err.message : 'Riprova'),
  });

  // Scritti nella finestra prima dell'anteprima (28/10/2026, dalla revisione): con l'anteprima
  // descrizione, priorità, promemoria e ricorrenza non si vedono più, e sparivano senza una
  // parola. Adesso vanno in ogni task, e una riga sotto l'anteprima lo dice, con il tasto per
  // non metterli.
  const promemoria = fromDateTimeInput(form.remindAt);
  const dallaFinestra: DallaFinestra = { descrizione: form.description, remindAt: promemoria, recurrence: form.recurrence };
  const scrittiPrima = [
    form.description.trim() ? 'la descrizione' : null,
    form.high ? 'la priorità alta' : null,
    promemoria ? `il promemoria del ${fmtDateTime(promemoria)}` : null,
    form.recurrence !== 'NONE' ? `la ricorrenza (${RECURRENCE_LABEL[form.recurrence].toLowerCase()})` : null,
  ].filter((p): p is string => !!p);
  const togliScrittiPrima = () => setForm({ ...form, description: '', high: false, remindAt: '', recurrence: 'NONE' });

  const { createMany } = useTaskActions();
  const spuntate = righe.filter((r) => r.spuntata);
  const n = spuntate.length;
  // Le cose da ricordare con la spunta, per indice nella proposta: i testi li scrive il server.
  const ricorda = proposta ? proposta.r.ricorda.map((_, i) => i).filter((i) => !ricordaTolte.has(i)) : [];
  const m = ricorda.length;
  const daSistemare = spuntate.find((r) => r.problema);
  // Quello che il server rifiuterebbe si dice qui, prima: il lotto si crea tutto o niente.
  const limite = !conAnteprima ? null
    : n > MAX_TASK ? `Al massimo ${MAX_TASK} task alla volta: togline ${n - MAX_TASK}`
    : daSistemare ? `Una riga va sistemata prima di creare: "${daSistemare.d.testo.slice(0, 60)}"`
    : null;
  const creaTutti = () => {
    setErrore(null);
    const tasks = spuntate.map((r) => corpoDi(r, projectId ?? null, dallaFinestra));
    // La finestra resta aperta con il testo e le spunte; il messaggio si legge qui, sopra il tasto.
    // Il server dice già cosa non va e in quale riga, contando le sole righe
    // spuntate ("Controlla i dati: titolo alla riga 2 (manca)").
    const onError = (err: unknown) => setErrore(err instanceof ApiError ? err.message : 'Non riesco a raggiungere il server: riprova');
    if (!proposta) {
      createMany.mutate({ tasks }, { onSuccess: () => onClose(), onError });
      return;
    }
    // «Crea 3 task e ricorda 2 cose»: una chiamata sola, con l'id della proposta. La task di
    // partenza si completa solo se la spunta resta e si crea almeno un task.
    const aiActionId = proposta.r.aiActionId;
    const completa = !!partenzaId && completaPartenza && n > 0;
    createMany.mutate({ tasks, aiActionId, completa, ricorda }, {
      onSuccess: (r) => {
        toastDellaProposta(r, aiActionId, qc);
        onClose();
      },
      onError: (err) => {
        // Proposta scaduta, già usata, non trovata o di un'altra persona: non si usa più. Si
        // toglie, così torna «Dividi di nuovo con MUNE», e la frase del server («La
        // proposta di MUNE è scaduta: dividi di nuovo») si legge lì sotto. Si decide
        // dal codice e non dallo stato (28/10/2026): un 404 «Task non trovato» arrivato a
        // metà lavoro non vuol dire che la proposta non c'è più.
        if (err instanceof ApiError && err.code === 'AI_PROPOSTA') {
          setProposta(null);
          setScelte({});
          setErroreAi(err.message);
          return;
        }
        // Fermato a metà, con le task già create: la frase del server dice cosa c'è già e
        // dove si annulla, e il toast ha l'Annulla. La finestra si chiude, perché rifarlo
        // darebbe dei doppioni.
        if (err instanceof ApiError && err.code === 'AI_A_META') {
          qc.invalidateQueries({ queryKey: ['tasks'] });
          qc.invalidateQueries({ queryKey: ['project'] });
          qc.invalidateQueries({ queryKey: ['ai', 'azioni'] });
          qc.invalidateQueries({ queryKey: ['ai', 'memoria'] });
          toast.warn('MUNE si è fermato a metà', {
            description: err.message,
            duration: 15_000,
            action: { label: 'Annulla', onClick: () => annullaDalToast(aiActionId, qc) },
          });
          onClose();
          return;
        }
        onError(err);
      },
    });
  };

  // Mentre l'Assistente divide non si crea niente, nemmeno con Ctrl+Invio: la proposta sta arrivando.
  const canSubmit = !dividendo && (conAnteprima
    ? (n > 0 || m > 0) && !limite && !createMany.isPending
    : !!titoloSolo && !create.isPending);
  const invia = () => {
    if (!canSubmit) return;
    if (conAnteprima) creaTutti();
    else create.mutate();
  };
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); invia(); }
  };
  const etichettaTasto = !conAnteprima ? 'Crea task'
    : proposta ? etichettaProposta(n, m, createMany.isPending)
    : createMany.isPending ? `Creo ${n} task…`
    : n === 0 ? 'Nessun task spuntato'
    : `Crea ${n} task`;

  // Il tasto dell'Assistente sotto il titolo. Con due righe o più l'anteprima a regole c'è già,
  // e dopo una proposta tolta lo stesso: «Dividi di nuovo con MUNE». È il tasto
  // principale (e «Crea task» no) quando il testo non sta in un titolo: oltre i 200 caratteri
  // o con più frasi. Se no resta secondario: l'Assistente si usa a richiesta (PIANO 1.1).
  const etichettaDividi = giaDiviso || multi ? 'Dividi di nuovo con MUNE' : 'Dividi con MUNE';
  const dividiPrincipale = conAssistente && !conAnteprima && (titoloSolo.length > TITOLO_MAX || piuFrasi(titoloSolo));

  const extra: ExtraProposta | undefined = proposta
    ? {
      lette: proposta.r.lette,
      finta: proposta.r.finta,
      ricorda: proposta.r.ricorda,
      ricordaTolte,
      onRicorda: (i, spuntata) => {
        setErrore(null);
        setRicordaTolte((prima) => {
          const dopo = new Set(prima);
          if (spuntata) dopo.delete(i);
          else dopo.add(i);
          return dopo;
        });
      },
      completa: partenzaId
        ? { spuntata: completaPartenza, onCambia: setCompletaPartenza, nota: notaPartenza(!!prefill?.partenzaPrevista, !!prefill?.partenzaRicorrente) }
        : null,
    }
    : undefined;

  // Il titolo cresce con il testo fino a sei righe, poi scorre. Con una riga è alto come il campo di prima.
  const titoloRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = titoloRef.current;
    if (!el) return;
    const cs = getComputedStyle(el);
    const riga = parseFloat(cs.lineHeight) || 20;
    const bordi = (parseFloat(cs.borderTopWidth) || 0) + (parseFloat(cs.borderBottomWidth) || 0);
    const vuoto = (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
    const massimo = riga * 6 + vuoto + bordi;
    el.style.height = 'auto';
    const voluta = el.scrollHeight + bordi;
    el.style.height = `${Math.min(voluta, massimo)}px`;
    el.style.overflowY = voluta > massimo ? 'auto' : 'hidden';
  }, [form.title, isMobile]);

  const campiPredefiniti = (
    <>
      <Field label="Assegnato a" span={6}>
        <select className="inp" value={form.assigneeId} onChange={(e) => setForm({ ...form, assigneeId: e.target.value })}>
          <OpzioniPersone users={users} meId={me?.id} />
        </select>
      </Field>
      <Field label="Scadenza" span={6}>
        <input className="inp mono" type="date" value={form.dueDate} onChange={(e) => setForm({ ...form, dueDate: e.target.value })} />
      </Field>
    </>
  );
  const campoCliente = (hint?: string) => (
    <Field label="Cliente" span={12} hint={hint}>
      <select className="inp" value={form.clientId} onChange={(e) => setForm({ ...form, clientId: e.target.value })}>
        <option value="">Nessuno</option>
        {(clients?.items ?? []).map((c) => <option key={c.id} value={c.id}>{c.shortName}</option>)}
      </select>
    </Field>
  );

  return (
    <Modal
      title="Nuovo task"
      kicker="Task"
      onClose={onClose}
      size="md"
      // Con l'anteprima la finestra si allarga (nuovo-task.css): le quattro etichette di una riga
      // stanno su una riga sola. Non size="lg": .modal-lg Tailwind la scarta (vedi lavori.css).
      className={conAnteprima ? 'modal--anteprima' : undefined}
      footer={
        <>
          {conAnteprima && (errore || limite) && (
            <div className="nt-avviso" role="alert">
              <Icon name="alert" size={14} />
              <span>{errore ?? limite}</span>
            </div>
          )}
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          {/* Un tasto principale per volta: quando lo è «Dividi con MUNE», questo no. */}
          <button className={`btn${dividiPrincipale ? '' : ' btn-primary'}`} onClick={invia} disabled={!canSubmit}>
            {etichettaTasto} {canSubmit && <Icon name="arrowRight" size={12} />}
          </button>
        </>
      }
    >
      <div className="form-grid" onKeyDown={onKeyDown}>
        <Field
          label="Titolo"
          required
          span={12}
          hint={!conAnteprima ? undefined
            : proposta ? (isMobile
              ? 'Si corregge toccando la riga. Se cambi il testo, la proposta di MUNE si toglie.'
              : 'In grigio quello proposto da MUNE o preso dalla finestra: si corregge con un clic sulle etichette. Se cambi il testo, la proposta si toglie.')
            : isMobile ? 'Ogni riga è un task: qui sotto cosa diventa. Si corregge toccando la riga.'
            : 'Ogni riga è un task: qui sotto cosa diventa. Si corregge con un clic sulle etichette.'}
        >
          <textarea
            ref={titoloRef}
            className="inp nt-titolo"
            rows={1}
            autoFocus
            placeholder="Cosa va fatto, una cosa per riga"
            aria-label="Titolo"
            value={form.title}
            // Mentre l'Assistente divide il testo resta quello mandato: la proposta è sua.
            readOnly={dividendo}
            onChange={(e) => cambiaTitolo(e.target.value)}
          />
        </Field>
        {conAssistente && !proposta && (
          <div className="nt-ai-riga" style={{ gridColumn: 'span 12' }}>
            <button
              type="button"
              className={`btn btn-sm nt-ai${dividiPrincipale && !dividendo ? ' btn-primary' : ''}`}
              onClick={chiediDivisione}
              disabled={dividendo || !form.title.trim()}
              aria-busy={dividendo || undefined}
            >
              {/* La mascotte pronta; mentre divide, quella che pensa con «Divido…». */}
              <span className="nt-ai-msc" aria-hidden="true">
                <Mascotte size={20} stato={dividendo ? 'pensa' : 'pronto'} />
              </span>
              <span>{dividendo ? 'Divido…' : etichettaDividi}</span>
            </button>
            {erroreAi && <div className="nt-ai-errore" role="alert">{erroreAi}</div>}
          </div>
        )}
        {conAnteprima ? (
          <>
            <div style={{ gridColumn: 'span 12' }}>
              <AnteprimaTask righe={righe} opz={opz} mobile={isMobile} onCambia={cambia} proposta={extra} />
            </div>
            <div className="nt-predefiniti" style={{ gridColumn: 'span 12' }}>
              Per le righe che non dicono chi lo fa, la scadenza o il cliente valgono questi:
            </div>
            {campiPredefiniti}
            {campoCliente()}
            {scrittiPrima.length > 0 && (
              <div className="nt-predefiniti nt-scritti" style={{ gridColumn: 'span 12' }}>
                <span>Vanno in ogni task anche quelli scritti prima: {elenco(scrittiPrima)}.</span>
                <button type="button" className="nt-tutte" onClick={togliScrittiPrima}>Non metterli nelle task</button>
              </div>
            )}
          </>
        ) : (
          <>
            {campiPredefiniti}
            {/* Sul telefono un task si crea al volo: titolo, chi lo fa e scadenza;
                il resto dietro "Altri dettagli". Sul computer mostraAltri è sempre
                vero e la finestra è quella di prima, campo per campo. */}
            {!mostraAltri && (
              <div style={{ gridColumn: 'span 12' }}>
                <button type="button" className="nt-altri" onClick={() => setAltri(true)}>
                  <span>
                    <strong>Altri dettagli</strong>
                    <em>promemoria, ricorrenza, priorità, note, cliente</em>
                  </span>
                  <Icon name="chevronD" size={16} />
                </button>
              </div>
            )}
            {mostraAltri && (<>
            <Field label="Promemoria" span={6} hint="Facoltativo, con l'ora">
              <input className="inp mono" type="datetime-local" value={form.remindAt} onChange={(e) => setForm({ ...form, remindAt: e.target.value })} />
            </Field>
            <Field label="Ricorrenza" span={6} hint="Facoltativa: al Completa si crea il prossimo">
              <select className="inp" value={form.recurrence} onChange={(e) => setForm({ ...form, recurrence: e.target.value as TaskRecurrenceKey })}>
                {RECURRENCES.map((r) => <option key={r} value={r}>{RECURRENCE_LABEL[r]}</option>)}
              </select>
            </Field>
            <Field label="Priorità" span={12}>
              <div className="seg-control">
                <button type="button" aria-pressed={!form.high} onClick={() => setForm({ ...form, high: false })}>Normale</button>
                <button type="button" aria-pressed={form.high} onClick={() => setForm({ ...form, high: true })}>Alta</button>
              </div>
            </Field>
            <Field label="Descrizione" span={12}>
              <textarea className="inp" rows={3} placeholder="Facoltativa: dettagli utili a chi lo farà" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Field>
            {campoCliente('Facoltativo')}
            </>)}
          </>
        )}
      </div>
    </Modal>
  );
}
